import React, { useEffect, useState } from "react";
import { FaHistory } from "react-icons/fa";
import AdminModal from "../../../components/AdminModal";
import FormField from "../../../components/FormField";
import { colors, spacing, typography, radius, inputBase, btnPrimary, btnSecondary } from "../../../utils/designTokens";
import OrdenDelDiaEditor from "./OrdenDelDiaEditor";
import { hoyMontevideo, msDeFechaHora } from "./registrada";

const VACIO = {
  tipo: "ordinaria",
  fecha: "",
  hora: "19:00",
  modalidad: "presencial",
  lugar: "",
  ordenDelDia: [{ titulo: "", detalle: "" }],
};

/**
 * "Registrar asamblea realizada": una asamblea que ya se hizo y no se convocó
 * por Habitia (asambleaAdmin op registrarRealizada). Sin citaciones ni sala:
 * después se sube la grabación en la sección Acta del detalle.
 * props: open, onClose, onSubmit(datos), guardando,
 *        errorServidor ({mensaje, campo} | null: `campo` lo manda el servidor
 *        en los errores de validación y se muestra en ese campo)
 */
export default function RegistrarRealizadaModal({ open, onClose, onSubmit, guardando, errorServidor = null }) {
  const [form, setForm] = useState(VACIO);
  const [errores, setErrores] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(VACIO);
    setErrores({});
  }, [open]);

  // Un error del servidor con campo va en ese campo; el resto, abajo del formulario.
  const campoServidor = errorServidor && errorServidor.campo;
  const errorDe = (campo) => errores[campo] || (campoServidor === campo ? errorServidor.mensaje : "");
  const errorGeneral =
    errorServidor && !["tipo", "fecha", "hora", "modalidad", "lugar", "ordenDelDia"].includes(campoServidor) ? errorServidor.mensaje : "";

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const remota = form.modalidad === "remota";

  const enviar = (e) => {
    e.preventDefault();
    const puntos = form.ordenDelDia.filter((p) => p.titulo.trim());
    const nuevos = {};
    if (!form.fecha) nuevos.fecha = "Elegí la fecha de la asamblea.";
    else if (!(msDeFechaHora(form.fecha, form.hora) < Date.now())) nuevos.fecha = "La fecha tiene que ser la de una asamblea que ya se hizo.";
    if (!remota && !form.lugar.trim()) nuevos.lugar = "Indicá el lugar de la asamblea.";
    if (!puntos.length) nuevos.ordenDelDia = "El orden del día necesita al menos un punto.";
    setErrores(nuevos);
    if (Object.keys(nuevos).length) return;
    onSubmit({
      tipo: form.tipo,
      fecha: form.fecha,
      hora: form.hora,
      modalidad: form.modalidad,
      lugar: remota ? "" : form.lugar.trim(),
      ordenDelDia: puntos.map((p) => ({ titulo: p.titulo.trim(), detalle: p.detalle })),
    });
  };

  const fila = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: spacing[4] };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Registrar asamblea realizada"
      size="wide"
      icon={<FaHistory />}
      footer={
        <>
          <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </button>
          <button type="submit" form="asamblea-realizada-form" style={btnPrimary} className="habitia-btn-primary" disabled={guardando}>
            {guardando ? "Registrando…" : "Registrar asamblea"}
          </button>
        </>
      }
    >
      <form id="asamblea-realizada-form" onSubmit={enviar} noValidate style={{ display: "flex", flexDirection: "column", gap: spacing[5] }}>
        <p style={S.explicacion}>
          Para una asamblea que ya se hizo y no se convocó por Habitia (por ejemplo, la primera del edificio). No se cita ni se avisa a
          nadie: después subís la grabación en la sección Acta y la IA arma el acta.
        </p>
        <div style={fila}>
          <FormField label="Tipo" htmlFor="asm-real-tipo" required>
            <select id="asm-real-tipo" className="habitia-input" style={inputBase} value={form.tipo} onChange={set("tipo")}>
              <option value="ordinaria">Ordinaria</option>
              <option value="extraordinaria">Extraordinaria</option>
            </select>
          </FormField>
          <FormField label="Fecha" htmlFor="asm-real-fecha" required error={errorDe("fecha")}>
            <input
              id="asm-real-fecha"
              type="date"
              max={hoyMontevideo()}
              className="habitia-input"
              style={inputBase}
              value={form.fecha}
              onChange={set("fecha")}
              aria-invalid={!!errorDe("fecha")}
            />
          </FormField>
          <FormField label="Hora" htmlFor="asm-real-hora" required error={errorDe("hora")}>
            <input id="asm-real-hora" type="time" className="habitia-input" style={inputBase} value={form.hora} onChange={set("hora")} />
          </FormField>
        </div>
        <div style={fila}>
          <FormField label="Modalidad" htmlFor="asm-real-modalidad" required error={errorDe("modalidad")}>
            <select id="asm-real-modalidad" className="habitia-input" style={inputBase} value={form.modalidad} onChange={set("modalidad")}>
              <option value="presencial">Presencial</option>
              <option value="remota">Remota (videollamada)</option>
              <option value="mixta">Mixta (presencial y videollamada)</option>
            </select>
          </FormField>
          {!remota && (
            <FormField label="Lugar" htmlFor="asm-real-lugar" required error={errorDe("lugar")}>
              <input
                id="asm-real-lugar"
                type="text"
                maxLength={300}
                className="habitia-input"
                style={inputBase}
                value={form.lugar}
                onChange={set("lugar")}
                placeholder="Salón del edificio"
                aria-invalid={!!errorDe("lugar")}
              />
            </FormField>
          )}
        </div>
        <OrdenDelDiaEditor
          puntos={form.ordenDelDia}
          onChange={(ordenDelDia) => setForm((f) => ({ ...f, ordenDelDia }))}
          error={errorDe("ordenDelDia")}
        />
        {errorGeneral && (
          <p role="alert" style={S.error}>
            {errorGeneral}
          </p>
        )}
      </form>
    </AdminModal>
  );
}

const S = {
  explicacion: {
    margin: 0,
    fontSize: typography.sizes.base,
    color: colors.infoDark,
    background: colors.infoLight,
    padding: spacing[3],
    borderRadius: radius.sm,
    lineHeight: 1.5,
  },
  error: {
    margin: 0,
    color: colors.errorDark,
    background: colors.errorLight,
    padding: spacing[3],
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
  },
};
